import Translation from 'components/Translation'
import { useAppContext } from 'context/AppContext'
import { useAuth } from 'context/AuthContext'
import { useTenantContext } from 'context/TenantContext'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import {
    CURRENCIES,
    Currency,
    getPriceFromContent,
    getTotalPrice,
} from 'types/currency'
import { RoleTypes } from 'types/staff'
import { classNames } from 'utils/dom-helpers'

function UpsellPricingSection(translations: Record<string, string>) {
    const router = useRouter()
    const { tiers } = useAppContext()
    const { hasRole, GlobalDialog, UpsellModal } = useAuth()
    const { tenant } = useTenantContext()
    const [currency, setCurrency] = useState<Currency>(CURRENCIES[0])
    const [current_tier, setCurrentTier] = useState<number>(0)

    useEffect(() => {
        if (!tenant?.chargebeeCustomerId) {
            setCurrentTier(0)
        } else {
            let tier = tiers.findIndex((t: { id: string | undefined }) => {
                return t.id === tenant?.tier?.id
            })
            setCurrentTier(tier)
        }
    }, [tenant])
    // TODO: Change currency based on user's saved preference

    const handleRedirect = function () {
        if (!hasRole([RoleTypes.SUPER_ADMIN, RoleTypes.ADMIN])) {
            GlobalDialog.setAttributes({
                title: 'Insufficient Permission',
                message:
                    "You don't have sufficient permission to do this. Please contact your Account Adminstrator.",
            })
            GlobalDialog.open()
        } else {
            UpsellModal.close()
            router.push('/dashboard/profile/')
        }
    }

    return (
        <div className="container mx-auto">
            <div className="grid lg:grid-cols-3 gap-12 sm:gap-6 xl:gap-8 xl:max-w-7xl mx-auto mt-12">
                {/* Freemium */}
                <div className="grid bg-white rounded-2xl shadow-2xl w-full py-10 px-8">
                    <figure className="text-center m-auto mt-2 text-primary-light">
                        <span className="rounded-lg p-0.5 pt-1 w-8 inline-block border-2 border-primary-light">
                            <i className="feather feather-user" />
                        </span>
                        <Translation
                            className="ml-2 circular text-lg font-medium"
                            content_key="pricing_tier_0_name"
                            render_as="span"
                            translations={translations}
                        />
                    </figure>
                    <Translation
                        className="block text-center mt-8"
                        content_key="pricing_tier_0_subtitle"
                        render_as="div"
                        translations={translations}
                    />
                    <div className="text-6xl block text-center text-primary circular-light mt-5">
                        {currency.symbol}
                        {getTotalPrice(
                            getPriceFromContent(
                                'pricing_tier_0_price_usd',
                                translations
                            ),
                            currency.conversion_rate
                        )}
                        <Translation
                            className="text-base text-gray-400 circular-light align-middle ml-3"
                            content_key="pricing_tier_0_subtitle_monthly"
                            render_as="span"
                            translations={translations}
                        />
                    </div>
                    <button
                        className={classNames(
                            'py-4 mt-5 text-base rounded-lg',
                            'md:text-xl',
                            current_tier == 0
                                ? 'bg-white border text-primary'
                                : 'bg-primary-light text-white'
                        )}
                        onClick={handleRedirect}
                    >
                        <Translation
                            content_key={
                                current_tier == 0
                                    ? 'Current Plan'
                                    : 'Downgrade Plan'
                            }
                            render_as="span"
                            translations={translations}
                        />
                    </button>
                </div>

                {/* Single Studio */}
                <div className="grid bg-white rounded-2xl shadow-2xl w-full py-10 px-8">
                    <figure className="text-center m-auto mt-2 text-primary flex items-center justify-center">
                        <i className="text-2xl feather feather-users flex items-center justify-center" />
                        <Translation
                            className="ml-2 circular text-lg flex items-center justify-center font-medium"
                            content_key="pricing_tier_1_name"
                            render_as="span"
                            translations={translations}
                        />
                    </figure>
                    <Translation
                        className="block text-center mt-8"
                        content_key="pricing_tier_1_subtitle"
                        render_as="div"
                        translations={translations}
                    />
                    <div className="text-6xl block text-center text-primary-dark circular-light mt-5">
                        {currency.symbol}
                        {getTotalPrice(
                            getPriceFromContent(
                                'pricing_tier_1_price_usd',
                                translations
                            ),
                            currency.conversion_rate
                        )}
                        <Translation
                            className="text-base text-gray-400 circular-light align-middle ml-3"
                            content_key="pricing_subtitle_monthly"
                            render_as="span"
                            translations={translations}
                        />
                    </div>
                    <button
                        className={classNames(
                            'py-4 mt-5 text-base rounded-lg',
                            'md:text-xl',
                            current_tier == 1
                                ? 'bg-white border text-primary'
                                : 'bg-primary-dark text-white'
                        )}
                        onClick={handleRedirect}
                    >
                        <Translation
                            content_key={
                                current_tier == 1
                                    ? 'Current Plan'
                                    : current_tier > 1
                                    ? 'Downgrade Plan'
                                    : 'Upgrade Plan'
                            }
                            render_as="span"
                            translations={translations}
                        />
                    </button>
                </div>

                {/* Multi-Location */}
                <div className="grid bg-white rounded-2xl shadow-2xl w-full py-10 px-8">
                    <figure className="text-center m-auto mt-2 text-secondary flex items-center justify-center">
                        <i className="text-2xl feather feather-map-pin flex items-center justify-center" />
                        <Translation
                            className="ml-2 circular text-lg flex items-center justify-center font-medium"
                            content_key="pricing_tier_2_name"
                            render_as="span"
                            translations={translations}
                        />
                    </figure>
                    <Translation
                        className="block text-center mt-8"
                        content_key="pricing_tier_2_subtitle"
                        render_as="div"
                        translations={translations}
                    />
                    <div className="text-6xl block text-center text-primary-dark circular-light mt-5">
                        {currency.symbol}
                        {getTotalPrice(
                            getPriceFromContent(
                                'pricing_tier_2_price_usd',
                                translations
                            ),
                            currency.conversion_rate
                        )}
                        <Translation
                            className="text-base text-gray-400 circular-light align-middle ml-3"
                            content_key="pricing_subtitle_monthly"
                            render_as="span"
                            translations={translations}
                        />
                    </div>
                    <button
                        className={classNames(
                            'py-4 mt-5 text-base rounded-lg',
                            'md:text-xl',
                            current_tier == 2
                                ? 'bg-white border text-primary'
                                : 'bg-secondary text-white'
                        )}
                        onClick={handleRedirect}
                    >
                        <Translation
                            content_key={
                                current_tier > 1
                                    ? 'Current Plan'
                                    : 'Upgrade Plan'
                            }
                            render_as="span"
                            translations={translations}
                        />
                    </button>
                </div>
            </div>
        </div>
    )
}

export default UpsellPricingSection
